import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  computeCompanyDeadlines,
  computePortfolioDeadlines,
} from '../utils/agmDeadlineEngine';

/**
 * useCompanyDeadlines — React hook that wraps the AGM deadline engine.
 *
 * Automatically computes deadlines for the active company and the full
 * portfolio using data already in AppContext. Re-computes only when
 * companies or activeCompany change (via useMemo).
 *
 * Usage:
 *   const { portfolio, activeDeadlines, getDeadlinesFor } = useCompanyDeadlines();
 */
export function useCompanyDeadlines() {
  const { companies, activeCompany } = useApp();

  /** Deadlines for the currently selected company (or null). */
  const activeDeadlines = useMemo(
    () => (activeCompany ? computeCompanyDeadlines(activeCompany) : null),
    [activeCompany],
  );

  /** Portfolio-level aggregation across all companies. */
  const portfolio = useMemo(
    () => computePortfolioDeadlines(companies),
    [companies],
  );

  /** Compute deadlines for any arbitrary company object on demand. */
  const getDeadlinesFor = (company) => computeCompanyDeadlines(company);

  return {
    /** Full portfolio aggregation: { results, alerts, stats, calendarEvents } */
    portfolio,

    /** Active company's deadlines (or null if none selected) */
    activeDeadlines,

    /** On-demand computation for a specific company */
    getDeadlinesFor,

    /** Shortcut: portfolio-level stats { total, overdue, dueSoon, compliant, pending } */
    stats: portfolio.stats,

    /** Shortcut: sorted alerts array */
    alerts: portfolio.alerts,

    /** Shortcut: calendar events array */
    calendarEvents: portfolio.calendarEvents,
  };
}

export default useCompanyDeadlines;
